/* eslint-disable no-nested-ternary */
// Pure, dependency-light formatting/derivation helpers shared by the
// Payments-tab advanced table (PaymentsTable.jsx), its actions cell, and its
// detail-panel timeline. Extracted out of the old card-based PaymentCard.jsx
// so the table and any future surface can reuse the exact same text/labels
// without re-deriving them. No React here — see PaymentTimelinePanel.jsx for
// the JSX row renderer.
import { parseApiDate } from 'src/utils/smartReminderRules';
import { derivePaymentCardStatus } from './paymentCardStatus';

// Brand green used for the WhatsApp channel icon everywhere payment UI shows
// it (Resend menu, timeline).
export const WHATSAPP_ICON_COLOR = '#25D366';

export const formatAmount = (n) => {
    const num = Number(n) || 0;
    return Number.isInteger(num) ? String(num) : num.toFixed(2);
};

export const titleCase = (t) => (typeof t === 'string' && t ? t.charAt(0).toUpperCase() + t.slice(1).toLowerCase() : '');

export const formatDate = (value, fmt) => {
    const parsed = parseApiDate(value);
    return parsed ? parsed.format(fmt) : '';
};

export const formatRelativeToNow = (value) => {
    const parsed = parseApiDate(value);
    if (!parsed) return '';
    return parsed.isAfter(new Date()) ? parsed.fromNow() : `expired ${parsed.fromNow()}`;
};

// A resend can land minutes or hours after the original request. When the
// newest delivery (any channel, across all checkout sessions) is more than
// ~2 minutes later than the request time, surface a "Last sent" hint so the
// requested time doesn't silently go stale after a resend. Same-day resends
// drop the date (just "16:41"); otherwise the full "24 Sep, 16:41".
const LAST_SENT_MIN_GAP_MINUTES = 2;

export function formatLastSent(lastDeliveryAt, requestedAt) {
    const lastMoment = parseApiDate(lastDeliveryAt);
    const requestedMoment = parseApiDate(requestedAt);
    if (!lastMoment || !requestedMoment) return null;
    if (lastMoment.diff(requestedMoment, 'minutes') <= LAST_SENT_MIN_GAP_MINUTES) return null;
    return lastMoment.isSame(requestedMoment, 'day') ? lastMoment.format('HH:mm') : lastMoment.format('DD MMM, HH:mm');
}

// Appointment-summary formatting — shared by the Payments-tab table's
// Appointment column (PaymentsTable.jsx) and its detail-panel header
// (PaymentTimelinePanel.jsx). Contract: Payment gains `appointment_summary`
// { id, datetime, type (CONSULTATION|TREATMENT), status
// (BOOKED|ATTENDED|CANCELLED|FAILED_TO_ATTEND), method } or absent
// entirely — every helper here tolerates that (renders '', null, etc).
export const APPOINTMENT_TYPE_LABEL = { CONSULTATION: 'Consultation', TREATMENT: 'Treatment' };

export const APPOINTMENT_STATUS_CHIP = {
    BOOKED: { label: 'Booked', color: 'info' },
    ATTENDED: { label: 'Attended', color: 'success' },
    CANCELLED: { label: 'Cancelled', color: 'default' },
    FAILED_TO_ATTEND: { label: 'FTA', color: 'warning' },
};

// "Consultation · 25 Sep, 09:00" text used by both the column cell and the
// detail-panel header.
export function formatAppointmentSummaryLine(appointmentSummary) {
    if (!appointmentSummary) return '';
    const typeLabel = APPOINTMENT_TYPE_LABEL[appointmentSummary.type] || titleCase(appointmentSummary.type) || 'Appointment';
    const dateLabel = formatDate(appointmentSummary.datetime, 'DD MMM, HH:mm');
    return dateLabel ? `${typeLabel} · ${dateLabel}` : typeLabel;
}

export const requestedBy = (payment) => {
    if (payment?.created_by_display) return payment.created_by_display;
    if (payment?.created_by) {
        const name = `${payment.created_by.first_name || ''} ${payment.created_by.last_name || ''}`.trim();
        if (name) return name;
    }
    return 'System';
};

// Short method labels for the compact table text ("Phone") and the
// mark-paid/mark-unpaid timeline event — distinct from PAYMENT_MODE_LABELS'
// longer "Received (...)" phrasing used for the status pill.
export const PAYMENT_METHOD_SHORT_LABEL = {
    PHONE: 'Phone',
    INPRACTICE: 'In practice',
    EMAIL: 'Email',
    BANK_TRANSFER: 'Bank transfer',
    OTHER: 'Other',
};

// The Method column / line-2 suffix: "Stripe link" whenever a checkout
// session has ever existed for this payment (paid or not — the payment was
// requested via a link), otherwise the recorded (manual) mode_of_payment.
export function paymentMethodLabel(payment) {
    const cs = payment?.checkout_summary || {};
    if (cs.has_open_checkout || cs.latest_checkout_session_id || cs.latest_is_expired || cs.latest_status) {
        return 'Stripe link';
    }
    const mode = payment?.mode_of_payment;
    if (!mode) return '—';
    return PAYMENT_METHOD_SHORT_LABEL[mode] || titleCase(mode);
}

// Valid resend channels are the ones the lead actually has contact details
// for — matches the guard already used by BookedPaymentStatusCard/
// CreateSendPaymentLinkDialog.
export function leadResendChannels(leadDetail) {
    const email = leadDetail?.lead_contact?.email || leadDetail?.email || '';
    const phone = leadDetail?.lead_contact?.phone || leadDetail?.phone || leadDetail?.phone_number || '';
    const channels = [];
    if (email) channels.push('email');
    if (phone) channels.push('whatsapp');
    return { channels, email, phone };
}

export const STATUS_PILL = {
    paid: { icon: '✅', color: 'success' },
    received: { icon: '💷', color: 'success' },
    awaiting: { icon: '⏳', color: 'warning' },
    expired: { icon: '⚠️', color: 'error' },
    failed: { icon: '❌', color: 'error' },
    noLink: { icon: '🔗', color: 'default' },
    refunded: { icon: '↩️', color: 'default' },
};

export const statusPillText = (status, payment) => {
    switch (status) {
        case 'paid':
            return `Paid ${formatDate(payment?.paid_at, 'DD MMM')}`;
        case 'received':
            // The method itself is shown as its own caption next to/under the
            // pill (see PaymentsTable.jsx's StatusCell) — no need to repeat it
            // in the pill text.
            return 'Received';
        case 'awaiting':
            return 'Awaiting payment';
        case 'expired':
            return 'Link expired';
        case 'failed':
            return 'Payment failed';
        case 'refunded':
            return 'Refunded';
        case 'noLink':
        default:
            return 'Not paid yet';
    }
};

// Sentence-case delivery state labels (mirrors the Timeline tab's convention
// — "Read", "Delivered", "Failed" — never the raw lowercase backend token).
export const DELIVERY_STATE_LABEL = {
    not_requested: 'Not requested',
    blocked: 'Blocked',
    failed: 'Failed',
    queued: 'Queued',
    accepted: 'Accepted',
    sent: 'Sent',
    delivered: 'Delivered',
    read: 'Read',
};

// Icon + colour per timeline event kind — same visual language (tinted-circle
// dot) as the lead Timeline tab (see Activities.jsx VERB_CONFIG): blue for
// link creation, brand green/blue for channel delivery (red on failure),
// green for paid, amber for expiry.
export const TIMELINE_EVENT_STYLE = {
    link: { icon: 'solar:wallet-money-bold', color: '#078DEE' },
    whatsapp: { icon: 'ic:baseline-whatsapp', color: '#25D366' },
    whatsappFailed: { icon: 'ic:baseline-whatsapp', color: '#FF5630' },
    email: { icon: 'ic:round-email', color: '#078DEE' },
    emailFailed: { icon: 'ic:round-email', color: '#FF5630' },
    paid: { icon: 'eva:checkmark-circle-2-fill', color: '#00A76F' },
    expiry: { icon: 'mdi:clock-time-three-outline', color: '#FFAB00' },
};

// Builds a single chronological (newest-first) event list for a payment's
// "View details" panel, merging together: each checkout session's creation,
// each channel's newest delivery per session, an upcoming/expired-link event
// (latest session only, unpaid only), and the paid/marked-paid event. Pure +
// independent of React so it's testable on its own.
export function buildPaymentTimelineEvents(payment, sessions) {
    const sessionList = Array.isArray(sessions) ? sessions : [];
    const multiSession = sessionList.length > 1;
    const sender = requestedBy(payment);
    const events = [];

    sessionList.forEach((s) => {
        if (s?.created_at) {
            events.push({
                id: `link-${s.id}`,
                icon: TIMELINE_EVENT_STYLE.link.icon,
                color: TIMELINE_EVENT_STYLE.link.color,
                title: `Payment link created by ${sender}`,
                timestamp: s.created_at,
                caption: multiSession ? `Payment link #${s.id}` : null,
            });
        }

        // Every delivery ATTEMPT gets its own row (not just the newest per
        // channel) — resends land as separate deliveries with their own
        // created_at, and each should appear chronologically so a user can
        // see e.g. a failed 11:43 send followed by successful resends at
        // 11:50, 12:43, 16:41.
        const sessionDeliveries = Array.isArray(s?.deliveries) ? s.deliveries : [];
        sessionDeliveries.forEach((d) => {
            const channel = String(d?.channel).toLowerCase();
            if (channel !== 'email' && channel !== 'whatsapp') return;
            const isFailed = d.state === 'failed' || d.state === 'blocked';
            const style = channel === 'whatsapp'
                ? (isFailed ? TIMELINE_EVENT_STYLE.whatsappFailed : TIMELINE_EVENT_STYLE.whatsapp)
                : (isFailed ? TIMELINE_EVENT_STYLE.emailFailed : TIMELINE_EVENT_STYLE.email);
            const stateLabel = DELIVERY_STATE_LABEL[d.state] || titleCase(d.state);
            const verb = channel === 'whatsapp' ? 'Sent on WhatsApp' : 'Sent by email';
            events.push({
                id: `delivery-${d.id}`,
                deliveryId: d.id,
                icon: style.icon,
                color: style.color,
                title: `${verb}${d.recipient_masked ? ` to ${d.recipient_masked}` : ''} — ${stateLabel}`,
                timestamp: d.created_at,
                error: d.error || null,
            });
        });

        const isLatestSession = String(s?.id) === String(payment?.checkout_summary?.latest_checkout_session_id);
        if (!payment?.paid_at && s?.expires_at && isLatestSession) {
            const parsedExpiry = parseApiDate(s.expires_at);
            const isExpired = Boolean(s.is_expired) || (parsedExpiry ? parsedExpiry.isBefore(new Date()) : false);
            events.push({
                id: `expiry-${s.id}`,
                icon: TIMELINE_EVENT_STYLE.expiry.icon,
                color: TIMELINE_EVENT_STYLE.expiry.color,
                title: isExpired ? 'Link expired' : 'Link expires',
                timestamp: s.expires_at,
            });
        }
    });

    const status = derivePaymentCardStatus(payment);
    if (status === 'received') {
        // Take Payment dialog "£X received over the phone/in practice" —
        // already-received money, whether or not `paid_at` has been
        // backfilled yet (legacy rows use created_at as the received date).
        const methodLabel = PAYMENT_METHOD_SHORT_LABEL[payment?.mode_of_payment] || titleCase(payment?.mode_of_payment);
        events.push({
            id: 'paid',
            icon: TIMELINE_EVENT_STYLE.paid.icon,
            color: TIMELINE_EVENT_STYLE.paid.color,
            title: `Received by ${payment?.paid_by_display || sender}${methodLabel ? ` · ${methodLabel}` : ''}`,
            timestamp: payment?.paid_at || payment?.created_at,
        });
    } else if (payment?.paid_at) {
        if (payment?.paid_manually) {
            const methodLabel = PAYMENT_METHOD_SHORT_LABEL[payment?.mode_of_payment] || titleCase(payment?.mode_of_payment);
            events.push({
                id: 'paid',
                icon: TIMELINE_EVENT_STYLE.paid.icon,
                color: TIMELINE_EVENT_STYLE.paid.color,
                title: `Marked as paid by ${payment?.paid_by_display || sender}${methodLabel ? ` · ${methodLabel}` : ''}`,
                timestamp: payment.paid_at,
                caption: payment?.paid_note || null,
            });
        } else {
            events.push({
                id: 'paid',
                icon: TIMELINE_EVENT_STYLE.paid.icon,
                color: TIMELINE_EVENT_STYLE.paid.color,
                title: `Paid £${formatAmount(payment.amount)}`,
                timestamp: payment.paid_at,
            });
        }
    }

    return events
        .filter((e) => e.timestamp)
        .sort((a, b) => (parseApiDate(b.timestamp)?.valueOf() || 0) - (parseApiDate(a.timestamp)?.valueOf() || 0));
}
